import React from 'react'
import { Button } from '../ui/Button'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import { LIFECYCLE_OPTIONS } from './informationSystemConstants'
import {
  fetchInformationSystems,
  fetchDataMap,
  fetchDataCategoryCatalog,
  createDataCategoryAssignment,
  deleteDataCategoryAssignment,
  type InformationSystemSummary,
} from '../../services/informationSystemsApi'
import DataMapDataFlowsSection from './DataMapDataFlowsSection'
import DataMapSummaryRow from './DataMapSummaryRow'
import DataMapDetailPanel, { DataMapDetailEmpty } from './DataMapDetailPanel'
import DataMapExecutiveWorkspace from '../enterprise-operational/DataMapExecutiveWorkspace'
import EnterpriseOperationalPage from '../enterprise-operational/EnterpriseOperationalPage'
import { featureFlags } from '../../config/featureFlags'
import type { DataMapRow } from './dataMapRowParts'
import styles from '../../styles/components/assets/AssetsDataMap.module.scss'

export default function DataMapTab() {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()

  const [systems, setSystems] = React.useState<InformationSystemSummary[]>([])
  const [dataMap, setDataMap] = React.useState<DataMapRow[]>([])
  const [catalog, setCatalog] = React.useState<string[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [searchQuery, setSearchQuery] = React.useState('')
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [flowsOpen, setFlowsOpen] = React.useState(true)
  const [submitting, setSubmitting] = React.useState(false)
  const [filterLifecycle, setFilterLifecycle] = React.useState('')
  const [filterCategory, setFilterCategory] = React.useState('')
  const [filterClassification, setFilterClassification] = React.useState('')
  const [filterInformationSystem, setFilterInformationSystem] = React.useState('')

  const load = React.useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const filters = {
        lifecycleStatus: filterLifecycle || undefined,
        category: filterCategory || undefined,
        classification: filterClassification || undefined,
      }
      const [sys, map, cat] = await Promise.all([
        fetchInformationSystems(token, orgId),
        fetchDataMap(token, orgId, filters),
        fetchDataCategoryCatalog(token),
      ])
      setSystems(Array.isArray(sys) ? sys : [])
      setDataMap(Array.isArray(map) ? map : [])
      setCatalog(Array.isArray(cat) ? cat : [])
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('assets.dataMap.error.load', 'Failed to load data map'))
    } finally {
      setLoading(false)
    }
  }, [token, orgId, t, filterLifecycle, filterCategory, filterClassification])

  React.useEffect(() => {
    load()
  }, [load])

  const filteredRows = React.useMemo(() => {
    let rows = dataMap
    const q = searchQuery.trim().toLowerCase()
    if (q) {
      rows = rows.filter(
        (row) =>
          row.informationSystemName.toLowerCase().includes(q) ||
          (row.informationSystemCode || '').toLowerCase().includes(q),
      )
    }
    if (filterInformationSystem) {
      rows = rows.filter((row) => row.informationSystemId === filterInformationSystem)
    }
    return rows
  }, [dataMap, searchQuery, filterInformationSystem])

  const selectedRow = React.useMemo(
    () => (selectedId ? dataMap.find((r) => r.informationSystemId === selectedId) : null),
    [dataMap, selectedId]
  )

  const handleAssign = async (payload: {
    category: string
    classification: string
    processingPurpose: string
    storageLocation: string
    retentionPeriod: string
    crossBorderTransfer: boolean
    notes: string
  }) => {
    if (!token || !selectedRow) return
    setSubmitting(true)
    try {
      await createDataCategoryAssignment(
        token,
        selectedRow.informationSystemId,
        {
          category: payload.category,
          classification: payload.classification || undefined,
          processingPurpose: payload.processingPurpose || undefined,
          storageLocation: payload.storageLocation || undefined,
          retentionPeriod: payload.retentionPeriod || undefined,
          crossBorderTransfer: payload.crossBorderTransfer,
          notes: payload.notes || undefined,
        },
        orgId
      )
      await load()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('assets.dataMap.error.assign', 'Failed to assign category'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleRemove = async (dataCategoryId: string) => {
    if (!token || !selectedRow) return
    try {
      await deleteDataCategoryAssignment(token, selectedRow.informationSystemId, dataCategoryId, orgId)
      await load()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('assets.dataMap.error.remove', 'Failed to remove category'))
    }
  }

  if (loading && dataMap.length === 0 && systems.length === 0) {
    return <p className={styles.loadingText}>{t('common.loading')}</p>
  }

  if (featureFlags.ENTERPRISE_OPERATIONAL_UX_V1 && systems.length > 0) {
    return (
      <EnterpriseOperationalPage>
        <DataMapExecutiveWorkspace
          systems={systems}
          dataMap={dataMap}
          catalog={catalog}
          loading={loading}
          error={error}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filterLifecycle={filterLifecycle}
          setFilterLifecycle={setFilterLifecycle}
          filterCategory={filterCategory}
          setFilterCategory={setFilterCategory}
          filterClassification={filterClassification}
          setFilterClassification={setFilterClassification}
          filterInformationSystem={filterInformationSystem}
          setFilterInformationSystem={setFilterInformationSystem}
          onRefresh={load}
          submitting={submitting}
          onAssign={handleAssign}
          onRemove={handleRemove}
        />
      </EnterpriseOperationalPage>
    )
  }

  if (systems.length === 0 && !error && !loading) {
    return (
      <div className={styles.dataMapWorkspace}>
        <p className={styles.introBanner}>
          {t(
            'assets.dataMap.intro',
            'Map data categories to information systems for processing context, storage, and cross-border transfer tracking.'
          )}
        </p>
        <div className={styles.emptyCard}>
          <p className={styles.emptyHint}>
            {t(
              'assets.dataMap.emptyNoSystems',
              'Data Map becomes useful after you create information systems. Create a system on the Information Systems tab first.'
            )}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.dataMapWorkspace}>
      <section className={styles.auxiliarySection}>
        <div className={styles.auxiliarySectionHeader}>
          <h2 className={styles.auxiliarySectionTitle}>
            {t('assets.dataMap.flowsSectionTitle', 'Data flows & export')}
          </h2>
          <button
            type="button"
            className={styles.collapseBtn}
            onClick={() => setFlowsOpen((v) => !v)}
            aria-expanded={flowsOpen}
          >
            {flowsOpen ? t('common.hide', 'Hide') : t('common.show', 'Show')}
          </button>
        </div>
        {flowsOpen ? (
          <DataMapDataFlowsSection
            systems={systems}
            catalog={catalog}
            onChanged={load}
            filterSystemId={filterInformationSystem || undefined}
          />
        ) : null}
      </section>

      <p className={styles.introBanner}>
        {t(
          'assets.dataMap.intro',
          'Map data categories to information systems for processing context, storage, and cross-border transfer tracking.'
        )}
      </p>

      <div className={styles.dmToolbar}>
        <div className={styles.toolbarLeft}>
          <div className={styles.toolbarPrimaryRow}>
            <input
              type="search"
              className={styles.searchField}
              placeholder={t('assets.dataMap.searchPlaceholder', 'Search systems…')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label={t('assets.dataMap.searchPlaceholder', 'Search systems')}
            />
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.lifecycle.status', 'Lifecycle')}</span>
              <select
                className={styles.selectField}
                value={filterLifecycle}
                onChange={(e) => setFilterLifecycle(e.target.value)}
              >
                <option value="">{t('assets.lifecycle.allStatuses', 'All statuses')}</option>
                {LIFECYCLE_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {t(`assets.lifecycle.${s}`, s)}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.dataMap.category', 'Data category')}</span>
              <select
                className={styles.selectField}
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="">{t('assets.dataMap.allCategories', 'All categories')}</option>
                {catalog.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.dataMap.system', 'Information system')}</span>
              <select
                className={styles.selectField}
                value={filterInformationSystem}
                onChange={(e) => setFilterInformationSystem(e.target.value)}
              >
                <option value="">{t('assets.dataMap.allSystems', 'All systems')}</option>
                {[...systems]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className={styles.filterFieldWrap}>
              <span className={styles.filterLabel}>{t('assets.dataMap.classification', 'Classification')}</span>
              <input
                className={styles.textField}
                style={{ maxWidth: '160px', width: '160px' }}
                value={filterClassification}
                onChange={(e) => setFilterClassification(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className={styles.toolbarRight}>
          <button type="button" className={styles.refreshBtn} onClick={() => load()}>
            {t('common.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      {error ? (
        <p className={styles.errorBanner}>
          {error}
          <Button type="button" size="small" color="outline" onClick={() => load()}>
            {t('common.retry', 'Retry')}
          </Button>
        </p>
      ) : null}

      {filteredRows.length === 0 ? (
        <div className={styles.emptyCard}>
          <p className={styles.emptyHint}>
            {t(
              'assets.dataMap.emptyFiltered',
              'No systems match the current filters. Adjust filters or assign data categories to a system.'
            )}
          </p>
        </div>
      ) : (
        <div className={styles.dmContentGrid}>
          <div className={styles.dmSystemsList}>
            {filteredRows.map((row) => (
              <DataMapSummaryRow
                key={row.informationSystemId}
                row={row}
                selected={selectedId === row.informationSystemId}
                onSelect={() => setSelectedId(row.informationSystemId)}
              />
            ))}
            {selectedRow ? (
              <div className={styles.mobilePanelBelow}>
                <DataMapDetailPanel
                  row={selectedRow}
                  catalog={catalog}
                  submitting={submitting}
                  onClose={() => setSelectedId(null)}
                  onRemove={handleRemove}
                  onAssign={handleAssign}
                />
              </div>
            ) : null}
          </div>
          <div className={styles.desktopPanelColumn}>
            {selectedRow ? (
              <DataMapDetailPanel
                row={selectedRow}
                catalog={catalog}
                submitting={submitting}
                onClose={() => setSelectedId(null)}
                onRemove={handleRemove}
                onAssign={handleAssign}
              />
            ) : (
              <DataMapDetailEmpty />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
